import AppConfig from '../../../lib/AppConfig';
import { cleanApiError, handleResponse } from '../../../utils/handleResponse';
import { getAPIHeaders, getAuthAPIHeaders } from '../../../utils/helpers';
import {
	AuthResponse,
	ImpersonateResponse,
	RequestOtpResponse,
	ValidateOtpResponse,
} from '../types/Auth';

export namespace AuthRepository {
	// Login
	export async function login(username: string, password: string): Promise<AuthResponse> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/authentication?returnClientList=false`;

			const headers = new Headers({
				Accept: 'application/json',
				'Content-Type': 'application/json;charset=utf-8',
				'Fineract-Platform-Tenantid': AppConfig.tenantId,
			});

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify({ username, password }),
			});

			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Login failed');
			throw cleanApiError(error);
		}
	}

	// impersonate
	export async function impersonate(
		payload: Record<string, string | number>,
	): Promise<ImpersonateResponse> {
		try {
			const url = `${AppConfig.api_url}/gmapis/impersonate`;
			const res = await fetch(url, {
				method: 'POST',
				headers: await getAPIHeaders(),
				body: JSON.stringify(payload),
			});
			return handleResponse(res);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	// Check 2FA Methods
	export async function check2faMethods(): Promise<{ name: string; target: string }[]> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/twofactor`;

			const response = await fetch(url, {
				headers: await getAuthAPIHeaders(),
			});

			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Failed to fetch 2FA methods');
			throw cleanApiError(error);
		}
	}

	// Request OTP
	export async function requestOtp(
		deliveryMethod: { name: string; target: string },
		extendedToken: boolean = false,
	): Promise<RequestOtpResponse> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/twofactor?deliveryMethod=${deliveryMethod.name}&extendedToken=${extendedToken}`;

			const headers = await getAuthAPIHeaders();
			headers.set('Content-type', 'application/json;charset=utf-8');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify({
					requestTime: Date.now(),
					tokenLiveTimeInSec: 86400,
					extendAccessToken: extendedToken,
					deliveryMethod,
				}),
			});

			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Failed to request OTP');
			throw cleanApiError(error);
		}
	}

	// Validate OTP
	export async function validateOtp(token: string): Promise<ValidateOtpResponse> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/twofactor/validate?token=${token}`;

			const headers = await getAuthAPIHeaders();
			headers.set('Content-type', 'application/json;charset=utf-8');

			const response = await fetch(url, {
				method: 'POST',
				headers,
			});

			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Failed to validate OTP');
			throw cleanApiError(error);
		}
	}

	// InValidates OTP
	export async function invalidateToken(token: string): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/twofactor/invalidate`;

			const headers = await getAPIHeaders();
			headers.set('Content-type', 'application/json;charset=utf-8');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify({
					token,
				}),
			});

			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Failed to validate OTP', error);
			return {};
		}
	}

	// Change Password
	export async function changePassword(userId: number, newPassword: string): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/gmapis/resetPassword/${userId}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'PUT',
				headers,
				body: JSON.stringify({ newPassword: newPassword, sendPasswordToEmail: true }),
			});
			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Failed to change password');
			throw cleanApiError(error);
		}
	}

	// Validate Password
	export async function validatePassowrd(
		username: string,
		password: string,
	): Promise<AuthResponse> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/authentication`;

			const headers = await getAuthAPIHeaders();
			headers.set('Content-type', 'application/json;charset=utf-8');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify({ username, password }),
			});

			const data = await handleResponse(response);

			return data;
		} catch (error) {
			console.log('Failed to change password');
			throw cleanApiError(error);
		}
	}

	// sendResetOtp
	export async function sendResetOtp(username: string, method: string): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/gmapis/requestResetCode`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify({ username, method }),
			});
			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Failed to change password');
			throw cleanApiError(error);
		}
	}

	// resetPassword
	export async function resetPassword(
		username: string,
		otp: string,
		newPassword: string,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/gmapis/resetPasswordOtp`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify({ username, otp, newPassword }),
			});
			const data = await handleResponse(response);
			return data;
		} catch (error) {
			console.log('Failed to change password');
			throw cleanApiError(error);
		}
	}
}
